<script setup>
import { ref, computed, reactive } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, email } from '@vuelidate/validators';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha';
import FormInput from '../../../../../components/Form/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PasswordRequirements from './PasswordRequirements.vue';
import { isValidPassword } from 'shared/helpers/Validators';
import GoogleOAuthButton from '../../../../../components/GoogleOauth/Button.vue';
import { register } from '../../../../../api/auth';
// FORK_PATCH_BEGIN: self-service-registration — drop business-only email validator

const MIN_PASSWORD_LENGTH = 6;

const store = useStore();
const { t } = useI18n();
const router = useRouter();

const hCaptcha = ref(null);
const isPasswordFocused = ref(false);
const isSignupInProgress = ref(false);

const credentials = reactive({
  email: '',
  password: '',
  accountName: '',
  userFullName: '',
  website: '',
  termsAccepted: false,
  hCaptchaClientResponse: '',
});

const rules = {
  credentials: {
    email: {
      required,
      email,
    },
    accountName: {
      required,
    },
    password: {
      required,
      isValidPassword,
      minLength: minLength(MIN_PASSWORD_LENGTH),
    },
    termsAccepted: {
      mustAccept: value => value === true,
    },
  },
};
// FORK_PATCH_END: self-service-registration

const v$ = useVuelidate(rules, { credentials });

const globalConfig = computed(() => store.getters['globalConfig/get']);

const termsCheckboxLabel = computed(() =>
  t('REGISTER_CUSTOM.TERMS.LABEL_HTML', {
    terms: globalConfig.value.termsURL || '#',
    privacy: globalConfig.value.privacyURL || '#',
  })
);

const termsAccepted = computed({
  get: () => credentials.termsAccepted,
  set: value => {
    credentials.termsAccepted = value;
  },
});

const allowedLoginMethods = computed(
  () => window.chatwootConfig.allowedLoginMethods || ['email']
);

const showGoogleOAuth = computed(
  () =>
    allowedLoginMethods.value.includes('google_oauth') &&
    Boolean(window.chatwootConfig.googleOAuthClientId)
);

const isFormValid = computed(() => !v$.value.$invalid);

const performRegistration = async () => {
  isSignupInProgress.value = true;
  try {
    const result = await register(credentials);
    if (result?.signedIn) return;

    router.push({
      name: 'auth_verify_email',
      state: { email: credentials.email },
    });
  } catch (error) {
    const errorMessage = error?.message || t('REGISTER.API.ERROR_MESSAGE');
    if (globalConfig.value.hCaptchaSiteKey) {
      hCaptcha.value.reset();
      credentials.hCaptchaClientResponse = '';
    }
    useAlert(errorMessage);
  } finally {
    isSignupInProgress.value = false;
  }
};

const submit = () => {
  if (isSignupInProgress.value) return;
  v$.value.$touch();
  if (v$.value.$invalid) return;
  isSignupInProgress.value = true;
  if (globalConfig.value.hCaptchaSiteKey) {
    hCaptcha.value.execute();
  } else {
    performRegistration();
  }
};

const onRecaptchaVerified = token => {
  credentials.hCaptchaClientResponse = token;
  performRegistration();
};

const onCaptchaError = () => {
  isSignupInProgress.value = false;
  credentials.hCaptchaClientResponse = '';
  hCaptcha.value.reset();
};
</script>

<template>
  <div class="flex-1">
    <form class="space-y-3" @submit.prevent="submit">
      <!-- FORK_PATCH_BEGIN: self-service-registration -->
      <FormInput
        v-model="credentials.accountName"
        name="account_name"
        :class="{ error: v$.credentials.accountName.$error }"
        :label="$t('REGISTER_CUSTOM.COMPANY.LABEL')"
        :placeholder="$t('REGISTER_CUSTOM.COMPANY.PLACEHOLDER')"
        :has-error="v$.credentials.accountName.$error"
        :error-message="$t('REGISTER_CUSTOM.COMPANY.ERROR')"
        @blur="v$.credentials.accountName.$touch"
      />
      <FormInput
        v-model="credentials.userFullName"
        name="user_full_name"
        :label="$t('REGISTER_CUSTOM.NAME.LABEL')"
        :placeholder="$t('REGISTER_CUSTOM.NAME.PLACEHOLDER')"
      />
      <FormInput
        v-model="credentials.website"
        type="url"
        name="website"
        :label="$t('REGISTER_CUSTOM.WEBSITE.LABEL')"
        :placeholder="$t('REGISTER_CUSTOM.WEBSITE.PLACEHOLDER')"
      />
      <!-- FORK_PATCH_END: self-service-registration -->
      <FormInput
        v-model="credentials.email"
        type="email"
        name="email_address"
        :class="{ error: v$.credentials.email.$error }"
        :label="$t('REGISTER.EMAIL.LABEL')"
        :placeholder="$t('REGISTER.EMAIL.PLACEHOLDER')"
        :has-error="v$.credentials.email.$error"
        :error-message="$t('REGISTER.EMAIL.ERROR')"
        @blur="v$.credentials.email.$touch"
      />
      <div class="relative">
        <FormInput
          v-model="credentials.password"
          type="password"
          name="password"
          :class="{ error: v$.credentials.password.$error }"
          :label="$t('LOGIN.PASSWORD.LABEL')"
          :placeholder="$t('SET_NEW_PASSWORD.PASSWORD.PLACEHOLDER')"
          :has-error="v$.credentials.password.$error"
          @focus="isPasswordFocused = true"
          @blur="
            isPasswordFocused = false;
            v$.credentials.password.$touch();
          "
        />
        <Transition
          enter-active-class="transition duration-200 ease-out origin-left"
          enter-from-class="opacity-0 scale-90 translate-x-1"
          enter-to-class="opacity-100 scale-100 translate-x-0"
          leave-active-class="transition duration-150 ease-in origin-left"
          leave-from-class="opacity-100 scale-100 translate-x-0"
          leave-to-class="opacity-0 scale-90 translate-x-1"
        >
          <PasswordRequirements
            v-if="isPasswordFocused"
            :password="credentials.password"
          />
        </Transition>
      </div>
      <VueHcaptcha
        v-if="globalConfig.hCaptchaSiteKey"
        ref="hCaptcha"
        size="invisible"
        :sitekey="globalConfig.hCaptchaSiteKey"
        @verify="onRecaptchaVerified"
        @error="onCaptchaError"
        @expired="onCaptchaError"
        @challenge-expired="onCaptchaError"
        @closed="onCaptchaError"
      />
      <!-- FORK_PATCH_BEGIN: self-service-registration -->
      <label
        class="flex gap-2 items-start text-sm text-n-slate-11 cursor-pointer"
        :class="{ 'text-n-ruby-10': v$.credentials.termsAccepted.$error }"
      >
        <input
          v-model="termsAccepted"
          type="checkbox"
          class="mt-0.5"
          @change="v$.credentials.termsAccepted.$touch"
        />
        <span
          class="[&>a]:text-n-brand [&>a]:font-medium [&>a]:hover:opacity-80"
          v-html="termsCheckboxLabel"
        />
      </label>
      <p
        v-if="v$.credentials.termsAccepted.$error"
        class="text-xs text-n-ruby-10 mb-0"
      >
        {{ $t('REGISTER_CUSTOM.TERMS.ERROR') }}
      </p>
      <!-- FORK_PATCH_END: self-service-registration -->
      <NextButton
        lg
        type="submit"
        data-testid="submit_button"
        class="w-full font-medium"
        :label="$t('REGISTER.SUBMIT')"
        :disabled="isSignupInProgress || !isFormValid"
        :is-loading="isSignupInProgress"
      />
    </form>
    <GoogleOAuthButton v-if="showGoogleOAuth" class="mt-3">
      {{ $t('REGISTER.OAUTH.GOOGLE_SIGNUP') }}
    </GoogleOAuthButton>
  </div>
</template>
